import * as React from "react";
import { Sparkles } from "lucide-react";
import { ui } from "@patient-watch/ui";

import { toast } from "@patient-watch/ui/sonner";
import {
    formBuilderQueryOptions,
    useApplyAiFormDraft,
    type FormBuilderData,
} from "@/features/forms/api/formBuilderApi";
import { useFormBuilderWorkspace } from "@/features/forms/components/form-builder/formBuilderWorkspace";
import { DraftWithAiDialog } from "./DraftWithAiDialog";
import { FormDraftSummary } from "./FormDraftPreview";
import { formDraftSchema, type FormDraft } from "./formDraft";
import { formBuilderDataToDraft, validateFormRevisionIds } from "./formRevision";
import { useDraftFormWithAi } from "./useDraftFormWithAi";
import { flushAutoSaves, useAutoSaveStatus } from "@/features/forms/components/form-builder/useAutoSave";
import { useQueryClient } from "@tanstack/react-query";

export function FormAiAssistant({ form }: { form: FormBuilderData }) {
    const draftAi = useDraftFormWithAi();
    const applyDraft = useApplyAiFormDraft(form.form_id);
    const workspace = useFormBuilderWorkspace();
    const queryClient = useQueryClient();
    const autoSaveStatus = useAutoSaveStatus();
    const [snapshot, setSnapshot] = React.useState(form);
    const currentDraft = React.useMemo(() => formBuilderDataToDraft(snapshot), [snapshot]);
    const [open, setOpen] = React.useState(false);
    const [prompt, setPrompt] = React.useState("");
    const [draft, setDraft] = React.useState<FormDraft | null>(null);
    const [warnings, setWarnings] = React.useState<string[]>([]);
    const [error, setError] = React.useState<string | null>(null);
    const requestId = React.useRef(0);

    const loadLatestForm = async () => {
        await flushAutoSaves();
        if (workspace) return workspace.getForm();
        await queryClient.invalidateQueries({ queryKey: ["form-builder", form.form_id] });
        return queryClient.fetchQuery(formBuilderQueryOptions(form.form_id));
    };

    if (form.is_published || form.is_licensed) return null;
    if (!formBuilderDataToDraft(form)) return null;

    const reset = () => {
        requestId.current += 1;
        setPrompt("");
        setDraft(null);
        setWarnings([]);
        setError(null);
        draftAi.reset();
        applyDraft.reset();
    };

    const discard = () => {
        reset();
        setOpen(false);
    };

    const generate = () => {
        if (!currentDraft) {
            setError("The current form could not be read. Try again.");
            return;
        }
        const id = ++requestId.current;
        setError(null);
        draftAi.mutate(
            { prompt, currentDraft },
            {
                onSuccess: (result) => {
                    if (id !== requestId.current) return;
                    if (!validateFormRevisionIds(currentDraft, result.draft)) {
                        setDraft(null);
                        setError("The AI changed internal form references. Retry with a more specific instruction.");
                        return;
                    }
                    setDraft(result.draft);
                    setWarnings(result.warnings);
                },
                onError: (caught) => {
                    if (id !== requestId.current) return;
                    setDraft(null);
                    setError(caught instanceof Error ? caught.message : "Could not edit the form. Try again.");
                },
            }
        );
    };

    const apply = () => {
        if (!draft) return;
        const validated = formDraftSchema.safeParse(draft);
        if (!validated.success || !currentDraft || !validateFormRevisionIds(currentDraft, validated.data)) {
            setError(
                validated.success
                    ? "The proposal no longer matches this form."
                    : (validated.error.issues[0]?.message ?? "Review the proposal before applying it.")
            );
            return;
        }
        setError(null);
        void loadLatestForm()
            .then((latest) => {
                const latestDraft = formBuilderDataToDraft(latest);
                if (!latestDraft || !validateFormRevisionIds(latestDraft, validated.data)) {
                    setError("The proposal no longer matches this form.");
                    return;
                }
                setSnapshot(latest);
                applyDraft.mutate(validated.data, {
                    onSuccess: discard,
                    onError: (caught) =>
                        setError(
                            caught instanceof Error ? caught.message : "Could not apply the form changes. Try again."
                        ),
                });
            })
            .catch((caught) =>
                setError(caught instanceof Error ? caught.message : "Resolve failed draft saves first.")
            );
    };

    return (
        <>
            <ui.Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                    void loadLatestForm()
                        .then((latest) => {
                            setSnapshot(latest);
                            setError(null);
                            setOpen(true);
                        })
                        .catch((caught) => {
                            toast.error(caught instanceof Error ? caught.message : "Resolve failed draft saves first.");
                        });
                }}
                disabled={autoSaveStatus === "saving" || autoSaveStatus === "error"}
            >
                <Sparkles className="h-4 w-4" /> Edit with AI
            </ui.Button>
            <DraftWithAiDialog
                open={open}
                onOpenChange={(nextOpen) => (nextOpen ? setOpen(true) : discard())}
                title="Edit this form with AI"
                description="Describe the changes you want. Review the proposal, then apply it to Form Builder. AI never publishes forms."
                placeholder="For example: simplify the wording, keep one question per page, and bold the important instructions."
                prompt={prompt}
                onPromptChange={setPrompt}
                generateLabel="Create proposal"
                retryLabel="Regenerate"
                confirmLabel="Apply changes"
                isGenerating={draftAi.isPending}
                isConfirming={applyDraft.isPending}
                error={error}
                warnings={warnings}
                hasPreview={draft != null}
                onGenerate={generate}
                onConfirm={apply}
                onDiscard={discard}
            >
                {draft ? (
                    <FormDraftSummary
                        draft={draft}
                        title="Proposal ready"
                        helper="Apply updates the form on this page. Edit the details in Form Builder. AI will not publish."
                    />
                ) : null}
            </DraftWithAiDialog>
        </>
    );
}
